import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Form, Formik } from "formik";
import Yup from "yup";
import get from "lodash/get";
import has from "lodash/has";
import find from "lodash/find";
import { withStyles } from "@mui/styles";

import {
  MenuItem,
  TextField,
  Select,
  Switch,
  FormControl,
  FormHelperText,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import SimpleSnackbar from "common/components/SimpleSnackbar";
import Resource from "common/components/Resource";
import ImageUploader from "core/ImageUploader";
import DragDropImageUploader from "core/DragDropImageUploader";
import { regions, countryCodes, regionCodes } from "common/location";
import OperatingHours from "common/components/OperatingHours";
import { handleHours } from "common/helpers/formik";
import { phoneNumberRegExp } from "common/utils/regexp";
import client from "common/admin/client";
import { FORM_SUBMISSION_ERROR } from "common/constants";
import SubmitButton from "common/components/SubmitButton";

const styles = (theme) => ({
  button: {
    marginTop: theme.spacing(2),
  },
  formControl: {
    margin: theme.spacing(1),
    width: "100%",
  },
});

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Building name is required"),
  street_address: Yup.string().required("Street address is required"),
  community_id: Yup.number().notOneOf([0], "Community is required").required(),
  city: Yup.string().required("City is required"),
  region: Yup.string()
    .required("Region is required")
    .oneOf(regionCodes, "Region is invalid"),
  country: Yup.string()
    .required("Country is required")
    .oneOf(countryCodes, "Country is invalid"),
  postal_code: Yup.string().required("Postal code is required"),
  primary_phone_number: Yup.string().matches(
    phoneNumberRegExp,
    "Phone Number must match the following format: 555-555-5555",
  ),
  website_url: Yup.string(),
  description: Yup.string(),
  complex_name: Yup.string(),
  tenant_id: Yup.number()
    .notOneOf([0], "Tenant is required")
    .required("Tenant is required"),
});

const getCity = (community) => client.get(`cities/${community.city_id}`);
const getTenants = () => client.get(`tenants`);

const BuildingForm = ({ classes, handleSubmit, initialValues, edit }) => {
  const [selectedCommunity, setSelectedCommunity] = useState(null);
  const [tenants, setTenants] = useState([]);

  useEffect(() => {
    getTenants()
      .then((data) => {
        const list = Array.isArray(data) ? data : data.data || [];
        setTenants(list);
      })
      .catch(console.error);
  }, []);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({
        handleChange,
        values,
        touched,
        errors,
        status,
        setValues,
        setFieldTouched,
        setFieldValue,
        setErrors,
        isSubmitting,
      }) => (
        <Form>
          <Grid container direction="column" spacing={2}>
            <Grid size={{ xs: 6 }}>
              <FormControl className={classes.formControl}>
                <TextField
                  error={touched.name && has(errors, "name")}
                  id="name"
                  type="text"
                  label="Building Name"
                  margin="normal"
                  helperText={touched.name && get(errors, "name")}
                  onChange={handleChange}
                  value={values.name}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FormControl className={classes.formControl}>
                <TextField
                  error={touched.complexName && has(errors, "complex_name")}
                  id="complex_name"
                  type="text"
                  label="Complex Name"
                  margin="normal"
                  helperText={
                    touched.complex_name && get(errors, "complex_name")
                  }
                  onChange={handleChange}
                  value={values.complex_name}
                />
              </FormControl>
            </Grid>
            <Resource
              name="communities"
              queryParams={{ pagination: false }}
              render={(communities) => {
                const currentCommunity = communities.find(
                  (cCom) => cCom.id === values.community_id,
                );
                setSelectedCommunity(currentCommunity);
                return (
                  <>
                    <Grid size={{ xs: 6 }}>
                      <FormControl
                        className={classes.formControl}
                        error={
                          touched.community_id && has(errors, "community_id")
                        }
                      >
                        <Select
                          name="community_id"
                          id="community_id"
                          value={values.community_id}
                          onChange={async (e) => {
                            const community = communities.find(
                              (c) => c.id === e.target.value,
                            );
                            setSelectedCommunity(community);
                            const city = await getCity(community);
                            if (!values.region) {
                              setFieldValue("region", city.region);
                            }
                            if (!values.city) {
                              setFieldValue("city", city.name);
                            }
                            if (!values.country) {
                              setFieldValue("country", city.country);
                            }

                            setFieldValue("community_id", community.id);
                          }}
                        >
                          {communities.map((community) => (
                            <MenuItem value={community.id} key={community.id}>
                              {community.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {touched.community_id && has(errors, "community_id") ? (
                          <FormHelperText>{errors.community_id}</FormHelperText>
                        ) : (
                          <FormHelperText>Neighborhood</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    {selectedCommunity?.multilingual !== null &&
                      selectedCommunity?.multilingual !== undefined && (
                        <Grid size={{ xs: 6 }}>
                          <FormControl className={classes.formControl}>
                            <Select
                              name="multilingual"
                              id="multilingual"
                              value={selectedCommunity.multilingual}
                              disabled={true}
                            >
                              <MenuItem value={false} key="English">
                                English
                              </MenuItem>
                              <MenuItem value={true} key="Bilingual">
                                Bilingual
                              </MenuItem>
                            </Select>
                            <FormHelperText>Language Preference</FormHelperText>
                          </FormControl>
                        </Grid>
                      )}
                  </>
                );
              }}
            />
            {!edit && (
              <Grid size={{ xs: 6 }}>
                <FormControl className={classes.formControl}>
                  <TextField
                    error={touched.floors && has(errors, "floors")}
                    id="floors"
                    type="number"
                    label="Number of floors"
                    margin="normal"
                    helperText={touched.floors && get(errors, "floors")}
                    onChange={(event) => {
                      if (event.target.value > 0) {
                        handleChange(event);
                      }
                    }}
                    value={values.floors}
                  />
                </FormControl>
              </Grid>
            )}
            <Grid size={{ xs: 6 }}>
              <FormControl className={classes.formControl}>
                <TextField
                  error={
                    touched.street_address && has(errors, "street_address")
                  }
                  id="street_address"
                  type="text"
                  label="Street Address"
                  margin="normal"
                  helperText={
                    touched.street_address && get(errors, "street_address")
                  }
                  onChange={handleChange}
                  value={values.street_address}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FormControl className={classes.formControl}>
                <TextField
                  error={touched.city && has(errors, "city")}
                  id="city"
                  type="text"
                  label="City"
                  margin="normal"
                  helperText={touched.city && get(errors, "city")}
                  onChange={handleChange}
                  value={values.city}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FormControl
                className={classes.formControl}
                error={touched.region && has(errors, "region")}
              >
                <Select
                  name="region"
                  id="region"
                  value={values.region}
                  onChange={(e) => {
                    const region = find(
                      regions,
                      (r) => r.code === e.target.value,
                    );
                    region &&
                      setValues({ ...values, country: region.countryCode });
                    return handleChange(e);
                  }}
                >
                  {regions.map((region) => (
                    <MenuItem value={region.code} key={region.code}>
                      {region.name}
                    </MenuItem>
                  ))}
                </Select>
                {touched.region && has(errors, "region") ? (
                  <FormHelperText>{errors.region}</FormHelperText>
                ) : (
                  <FormHelperText>Region</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FormControl className={classes.formControl}>
                <TextField
                  error={touched.postal_code && has(errors, "postal_code")}
                  id="postal_code"
                  type="text"
                  label="Postal Code"
                  margin="normal"
                  onChange={handleChange}
                  value={values.postal_code}
                  helperText={touched.postal_code && get(errors, "postal_code")}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FormControl className={classes.formControl}>
                <TextField
                  error={touched.description && has(errors, "description")}
                  id="description"
                  type="text"
                  rows="4"
                  label="Description"
                  margin="normal"
                  helperText={touched.description && get(errors, "description")}
                  onChange={handleChange}
                  value={values.description}
                  multiline
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FormControl className={classes.formControl}>
                <TextField
                  error={touched.website_url && has(errors, "website_url")}
                  id="website_url"
                  type="text"
                  label="Website"
                  margin="normal"
                  helperText={touched.website_url && get(errors, "website_url")}
                  onChange={handleChange}
                  value={values.website_url}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FormControl className={classes.formControl}>
                <TextField
                  error={
                    touched.primary_phone_number &&
                    has(errors, "primary_phone_number")
                  }
                  id="primary_phone_number"
                  type="text"
                  label="Phone Number"
                  margin="normal"
                  helperText={
                    touched.primary_phone_number &&
                    get(errors, "primary_phone_number")
                  }
                  onChange={handleChange}
                  value={values.primary_phone_number}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FormControl
                className={classes.formControl}
                error={touched.tenant_id && has(errors, "tenant_id")}
              >
                <Select
                  value={values.tenant_id || ""}
                  name="tenant_id"
                  id="tenant_id"
                  onChange={(e) => {
                    const selectedTenant = tenants.find(
                      (t) => t.id === e.target.value,
                    );
                    setFieldValue("tenant_id", e.target.value);
                    setFieldValue(
                      "is_hilo",
                      selectedTenant?.code === "hilo-application",
                    );
                  }}
                  disabled={edit}
                >
                  {tenants.map((tenant) => (
                    <MenuItem value={tenant.id} key={tenant.id}>
                      {tenant.display_name}
                    </MenuItem>
                  ))}
                </Select>
                {touched.tenant_id && has(errors, "tenant_id") ? (
                  <FormHelperText>{errors.tenant_id}</FormHelperText>
                ) : (
                  <FormHelperText>Tenant</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FormHelperText>Operating Hours</FormHelperText>
              <OperatingHours
                handleChange={handleHours(setFieldValue)}
                hours={values.hours}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FormControl className={classes.formControl}>
                <FormHelperText>Active?</FormHelperText>
                <Switch
                  id="active"
                  name="active"
                  checked={values.active}
                  onChange={handleChange}
                  color="primary"
                />
                {touched.active && has(errors, "active") ? (
                  <FormHelperText>{errors.active}</FormHelperText>
                ) : null}
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FormControl className={classes.formControl}>
                <FormHelperText>Hide in Sign up?</FormHelperText>
                <Switch
                  id="sign_up_inactive"
                  name="sign_up_inactive"
                  checked={values.sign_up_inactive}
                  onChange={handleChange}
                  color="primary"
                />
                {touched.sign_up_inactive && has(errors, "sign_up_inactive") ? (
                  <FormHelperText>{errors.sign_up_inactive}</FormHelperText>
                ) : null}
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <ImageUploader
                buttonId="logo_image_uploader_btn"
                inputId="logo_image_input"
                buttonContainer={{
                  alignItems: "center",
                  display: "flex",
                  marginTop: 10,
                  marginLeft: 80,
                }}
                imageStyle={{
                  width: "100%",
                  borderRadius: "50%",
                  height: "100%",
                }}
                container={{ flexDirection: "column", marginBottom: 16 }}
                imageContainer={{
                  height: 300,
                  width: 300,
                  borderRadius: "50%",
                }}
                label="Upload Logo"
                caption="Recommended dimensions 300px x 300px"
                initialImageUrl={values.logoUrl}
                touched={touched.logoUrl}
                onChange={(file) => {
                  setFieldTouched("logoUrl");
                  setFieldValue("logoFile", file, false);
                }}
              />
            </Grid>
            {/* <Grid size={{ xs: 6 }}>
            <ImageUploader
              label="Upload Hero"
              multiple
              caption="Recommended dimensions 1125px x 639px"
              initialImageUrl={
                initialValues.otherHeroUrl
                  ? `${values.heroUrl},${initialValues.otherHeroUrl}`
                  : values.heroUrl
              }
              touched={touched.heroUrl}
              onError={(text) => {
                setErrors({ heroUrl: text });
              }}
              onChange={(file) => {
                setFieldTouched("heroUrl");
                setFieldValue("heroFile", file, false);
              }}
            />
            {has(errors, "heroUrl") ? (
              <FormHelperText>{errors.heroUrl}</FormHelperText>
            ) : null}
          </Grid> */}
            <Grid size={{ xs: 12 }}>
              <DragDropImageUploader
                label="Upload Hero"
                caption="Recommended dimensions 1125px x 639px"
                initialImageUrl={
                  initialValues.otherHeroUrl
                    ? `${values.heroUrl},${initialValues.otherHeroUrl}`
                    : values.heroUrl
                }
                onChange={(file) => {
                  setFieldTouched("heroUrl");
                  setFieldValue("heroFile", file, false);
                }}
                maxCount={10}
                errorMessage={errors.heroUrl}
                onError={(text) => {
                  setErrors({ heroUrl: text });
                }}
                accept={"image/*"}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <SubmitButton isSubmitting={isSubmitting}>
                {edit ? "Update Building" : "Create Building"}
              </SubmitButton>
            </Grid>
            <Grid size={{ xs: 6 }}>
              {has(errors, "http") && (
                <SimpleSnackbar message={FORM_SUBMISSION_ERROR} />
              )}
            </Grid>
            {get(status, "success") && (
              <SimpleSnackbar
                message={edit ? "Building updated" : "New building created"}
              />
            )}
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

BuildingForm.defaultProps = {
  edit: false,
};

BuildingForm.propTypes = {
  classes: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.object.isRequired,
  edit: PropTypes.bool.isRequired,
};

export default withStyles(styles)(BuildingForm);
